import { io } from 'socket.io-client';
import { config } from 'src/config-global';

const CONNECT_TIMEOUT_MS = 10000;
const AUTH_SYNC_TIMEOUT_MS = 3000;
const JOIN_TIMEOUT_MS = 3000;
const LEAVE_TIMEOUT_MS = 1500;
const HEALTH_CHECK_TIMEOUT_MS = 2500;
const HIDDEN_THRESHOLD_MS = 5000;
const RECONNECT_GAP_MS = 100;

function createSocketManager(role) {
  let socket = null;
  let connectPromise = null;
  let authOp = 0;

  // auth:sync coalescing: one request in flight, at most one queued behind it.
  // The queued run always reflects the LATEST session (e.g. a login that
  // happened while a guest sync was still in flight), so it is never dropped.
  let syncInFlight = null;
  let syncQueued = null;

  // Prevents overlapping health checks / reconnects from stacking up when
  // visibilitychange, pageshow and online all fire together (iOS does this).
  let recoveryInFlight = null;

  function resetSocketState(sock) {
    if (!sock) return;

    sock.__joinedUserId = null;
    sock.__joinedAdmin = false;
    sock.__desiredUserRoomId = null;
    sock.__desiredAdminRoom = false;
  }

  const ensureConnected = () => {
    // eslint-disable-next-line no-use-before-define
    const sock = getSocket();

    if (sock.connected) return Promise.resolve(sock);

    if (!connectPromise) {
      connectPromise = new Promise((resolve, reject) => {
        let settled = false;
        let timer = null;

        const onConnect = () => {
          // eslint-disable-next-line no-use-before-define
          finish(resolve, sock);
        };

        const onError = (error) => {
          // eslint-disable-next-line no-use-before-define
          finish(reject, error);
        };

        const cleanup = () => {
          clearTimeout(timer);
          sock.off('connect', onConnect);
          sock.off('connect_error', onError);
          connectPromise = null;
        };

        const finish = (fn, value) => {
          if (settled) return;
          settled = true;
          cleanup();
          fn(value);
        };

        timer = setTimeout(() => {
          finish(reject, new Error(`${role} socket connect timeout`));
        }, CONNECT_TIMEOUT_MS);

        sock.once('connect', onConnect);
        sock.once('connect_error', onError);

        sock.connect();
      });
    }

    return connectPromise;
  };

  const runAuthSync = async () => {
    const sock = await ensureConnected();

    return new Promise((resolve) => {
      sock.timeout(AUTH_SYNC_TIMEOUT_MS).emit('auth:sync', (err, res) => {
        if (err) {
          resolve({
            ok: false,
            code: 'AUTH_SYNC_TIMEOUT',
            message: 'auth:sync timeout',
          });
          return;
        }

        resolve(res || { ok: false, message: 'Empty auth:sync response' });
      });
    });
  };

  const authSync = (opId) => {
    if (opId && opId !== authOp) {
      return Promise.resolve({ ok: false, cancelled: true });
    }

    if (!syncInFlight) {
      syncInFlight = runAuthSync()
        .catch((error) => ({
          ok: false,
          code: 'AUTH_SYNC_FAILED',
          message: error?.message || 'auth:sync failed',
        }))
        .finally(() => {
          syncInFlight = null;
        });

      return syncInFlight;
    }

    if (!syncQueued) {
      syncQueued = syncInFlight.then(() => {
        syncQueued = null;
        return authSync();
      });
    }

    return syncQueued;
  };

  const joinUserRoom = async (userId) => {
    const sock = await ensureConnected();
    let desiredId = null;
    if (userId) {
      desiredId = String(userId);
    }

    sock.__desiredUserRoomId = desiredId;

    if (!desiredId) {
      return {
        ok: false,
        code: 'MISSING_USER_ID',
        message: 'Missing userId',
      };
    }

    if (sock.__joinedUserId && String(sock.__joinedUserId) === desiredId) {
      return {
        ok: true,
        skipped: true,
        userId: desiredId,
        message: 'Already joined user room',
      };
    }

    return new Promise((resolve) => {
      sock.timeout(JOIN_TIMEOUT_MS).emit('join', desiredId, (err, res) => {
        if (err) {
          sock.__joinedUserId = null;

          resolve({
            ok: false,
            code: 'JOIN_USER_ROOM_TIMEOUT',
            message: 'join user room timeout',
          });
          return;
        }

        if (res?.ok) {
          sock.__joinedUserId = desiredId;
        } else {
          sock.__joinedUserId = null;
          console.log(`❌ [${role}] join user room failed:`, res);
        }

        resolve(res || { ok: false, message: 'Empty join response' });
      });
    });
  };

  const joinAdminRoom = async () => {
    const sock = await ensureConnected();

    sock.__desiredAdminRoom = true;

    if (sock.__joinedAdmin) {
      return {
        ok: true,
        skipped: true,
        message: 'Already joined admin room',
      };
    }

    return new Promise((resolve) => {
      sock.timeout(JOIN_TIMEOUT_MS).emit('join-admin', (err, res) => {
        if (err) {
          sock.__joinedAdmin = false;

          resolve({
            ok: false,
            code: 'JOIN_ADMIN_ROOM_TIMEOUT',
            message: 'join admin room timeout',
          });
          return;
        }

        if (res?.ok) {
          sock.__joinedAdmin = true;
        } else {
          sock.__joinedAdmin = false;
          console.log(`❌ [${role}] join admin room failed:`, res);
        }

        resolve(res || { ok: false, message: 'Empty join-admin response' });
      });
    });
  };

  // Is the transport actually alive? On iOS/WebKit a backgrounded tab can
  // report connected:true while the transport is frozen. A round-trip ack
  // tells the truth without tearing down a healthy connection.
  const isTransportHealthy = (sock) => {
    if (!sock || !sock.connected) return Promise.resolve(false);

    return new Promise((resolve) => {
      sock.timeout(HEALTH_CHECK_TIMEOUT_MS).emit('auth:sync', (err) => {
        resolve(!err);
      });
    });
  };

  // iOS/WebKit freezes the transport when backgrounded and does not fire
  // onclose, so the socket reports connected while actually dead.
  //  - close the ENGINE, not the socket: socket.disconnect() is a manual
  //    disconnect, and state recovery only survives an UNEXPECTED close.
  //  - leave a gap before reconnecting; a connect() issued mid-teardown
  //    can no-op and strand the socket.
  const hardReconnect = (sock, reason) => {
    console.log(`♻️ [${role}] hardReconnect (${reason})`);

    try {
      const engine = sock.io?.engine;

      if (engine) {
        engine.close();
      } else if (sock.connected) {
        sock.disconnect();
      }
    } catch (e) {
      console.log(`⚠️ [${role}] engine close failed:`, e?.message || e);
    }

    setTimeout(() => {
      if (!sock.connected) {
        sock.connect();
      }
    }, RECONNECT_GAP_MS);
  };

  // Only reconnect when the connection is really broken. Reconnecting a
  // healthy socket on every app-switch caused missed live updates, duplicate
  // room joins and status flicker.
  const recoverIfStale = (sock, reason) => {
    if (recoveryInFlight) return recoveryInFlight;

    recoveryInFlight = (async () => {
      try {
        if (!sock.connected) {
          // socket.io is either already reconnecting or gave up; nudge it.
          if (!sock.active) {
            sock.connect();
          }
          return;
        }

        const healthy = await isTransportHealthy(sock);

        if (!healthy) {
          hardReconnect(sock, `${reason} (health check failed)`);
        }
      } finally {
        recoveryInFlight = null;
      }
    })();

    return recoveryInFlight;
  };

  function getSocket() {
    if (!socket) {
      socket = io(config.socketUrl, {
        withCredentials: true,
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 5000,
        timeout: CONNECT_TIMEOUT_MS,
      });

      socket.__role = role;
      resetSocketState(socket);

      const sock = socket;
      let hiddenSince = null;

      // Re-establish server-side rooms after every (re)connect. Rooms live on
      // the server per connection, so a new connection starts with none.
      const reassertRooms = async () => {
        try {
          await authSync();

          if (sock.__desiredUserRoomId) {
            await joinUserRoom(sock.__desiredUserRoomId);
          }

          if (sock.__desiredAdminRoom) {
            await joinAdminRoom();
          }
        } catch (e) {
          console.log(`⚠️ [${role}] reassertRooms failed:`, e?.message || e);
        }
      };

      sock.on('connect', () => {
        sock.__joinedUserId = null;
        sock.__joinedAdmin = false;

        reassertRooms();
      });

      sock.on('disconnect', (reason) => {
        sock.__joinedUserId = null;
        sock.__joinedAdmin = false;

        console.log(`⚠️ [${role}] socket disconnected:`, reason);
      });

      sock.on('connect_error', (error) => {
        console.log(`❌ [${role}] socket connect_error:`, error?.message || error);
      });

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            hiddenSince = Date.now();
            return;
          }

          if (document.visibilityState !== 'visible' || hiddenSince === null) {
            return;
          }

          const hiddenFor = Date.now() - hiddenSince;
          hiddenSince = null;

          if (hiddenFor > HIDDEN_THRESHOLD_MS) {
            recoverIfStale(sock, `visible after ${hiddenFor}ms`);
          }
        });
      }

      if (typeof window !== 'undefined') {
        // iOS restores from bfcache on app-switch WITHOUT firing
        // visibilitychange — often the real return path on device.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            hiddenSince = null;
            recoverIfStale(sock, 'pageshow-bfcache');
          }
        });

        window.addEventListener('online', () => {
          recoverIfStale(sock, 'online');
        });
      }
    }

    return socket;
  }

  const nextAuthOp = () => {
    authOp += 1;
    return authOp;
  };

  const isLatest = (id) => id === authOp;

  const leaveAllRooms = async () => {
    if (!socket || !socket.connected) {
      resetSocketState(socket);

      return {
        ok: true,
        skipped: true,
      };
    }

    const sock = socket;

    return new Promise((resolve) => {
      sock.timeout(LEAVE_TIMEOUT_MS).emit('leave-all-rooms', (err, res) => {
        resetSocketState(sock);

        if (err) {
          resolve({
            ok: false,
            code: 'LEAVE_ALL_ROOMS_TIMEOUT',
            message: 'leave-all-rooms timeout',
          });
          return;
        }

        resolve(res || { ok: true });
      });
    });
  };

  // Logout / wallet-disconnect path.
  //
  // The socket stays up (prices and heartbeat are global broadcasts a guest
  // should still receive) and is downgraded: leave the user/admin rooms, then
  // re-sync so the server re-derives rooms from the now-empty session.
  const downgradeToGuest = async () => {
    if (!socket) return { ok: true, skipped: true };

    // Invalidate any in-flight auth ops tied to the session being ended.
    authOp += 1;

    if (!socket.connected) {
      resetSocketState(socket);

      try {
        await ensureConnected();
      } catch (e) {
        console.log(`⚠️ [${role}] guest reconnect failed:`, e?.message || e);

        return { ok: false, code: 'GUEST_RECONNECT_FAILED' };
      }
    }

    await leaveAllRooms();
    await authSync();

    return { ok: true };
  };

  // Full teardown. Kept for genuine shutdown, NOT used for logout.
  const disconnectSocket = async () => {
    if (!socket) return;

    authOp += 1;
    connectPromise = null;

    try {
      if (socket.connected) {
        await leaveAllRooms();
        socket.disconnect();
      }
    } catch (e) {
      socket.disconnect();
      console.error(`❌ [${role}] disconnect error:`, e);
    } finally {
      resetSocketState(socket);
    }
  };

  const reconnectSocket = async () => {
    await disconnectSocket();

    const sock = await ensureConnected();

    return sock;
  };

  const handleLogoutSocket = async () => {
    await downgradeToGuest();
  };

  return {
    getSocket,
    ensureConnected,
    reconnectSocket,
    nextAuthOp,
    isLatest,
    authSync,
    joinUserRoom,
    joinAdminRoom,
    leaveAllRooms,
    downgradeToGuest,
    disconnectSocket,
    handleLogoutSocket,
  };
}

const clientSocketManager = createSocketManager('client');
const adminSocketManager = createSocketManager('admin');

// Client exports
export const getClientSocket = clientSocketManager.getSocket;
export const ensureClientConnected = clientSocketManager.ensureConnected;
export const reconnectClientSocket = clientSocketManager.reconnectSocket;
export const nextClientAuthOp = clientSocketManager.nextAuthOp;
export const isLatestClientAuthOp = clientSocketManager.isLatest;
export const clientAuthSync = clientSocketManager.authSync;
export const { joinUserRoom } = clientSocketManager;
export const handleClientLogoutSocket = clientSocketManager.handleLogoutSocket;
export const disconnectClientSocket = clientSocketManager.disconnectSocket;

// Admin exports
export const getAdminSocket = adminSocketManager.getSocket;
export const ensureAdminConnected = adminSocketManager.ensureConnected;
export const reconnectAdminSocket = adminSocketManager.reconnectSocket;
export const nextAdminAuthOp = adminSocketManager.nextAuthOp;
export const isLatestAdminAuthOp = adminSocketManager.isLatest;
export const adminAuthSync = adminSocketManager.authSync;
export const { joinAdminRoom } = adminSocketManager;
export const handleAdminLogoutSocket = adminSocketManager.handleLogoutSocket;
export const disconnectAdminSocket = adminSocketManager.disconnectSocket;
